// The permission strings the API sends in `AdminDto.permissions`, named for the console.
// Each value is checked against the API's `AdminPermission` enum, and the list must name
// every member of it: drift here would make permission-gated nav items vanish for every
// admin, so it is a type error instead.
import type { Covers, Schemas } from './schemas';

export type AdminPermission = Schemas['AdminPermission'];

export const ADMIN_PERMISSIONS = {
  /** Who may approve / reject verification requests (orgs & talents). */
  ORGS_VERIFY: 'verifications.review',
  /** Who may approve / reject document uploads in the audit queue. */
  DOCUMENTS_AUDIT: 'documents.audit',
  /** Who may view and edit talent profiles. */
  TALENTS_MANAGE: 'candidates.manage',
  /** Who may view and edit organisation accounts. */
  ORGS_MANAGE: 'organizations.manage',
  /** Who may create, disable and re-role admin accounts. */
  ADMINS_MANAGE: 'admins.manage',
  /** Who may read the append-only audit trail. */
  AUDIT_LOG_READ: 'audit_log.read',
  /** Who may read platform-wide analytics reports. */
  REPORTS_READ: 'reports.read',
  /** Who may reprice subscription plans. */
  PLANS_MANAGE: 'plans.manage',
  /** Who may read the support inbox, reply to a ticket and close it. */
  SUPPORT_MANAGE: 'support.manage',
} as const satisfies Record<string, AdminPermission>;
const _adminPermissionsCovered: Covers<
  AdminPermission,
  (typeof ADMIN_PERMISSIONS)[keyof typeof ADMIN_PERMISSIONS]
> = true;

export const ALL_ADMIN_PERMISSIONS: readonly AdminPermission[] = Object.values(ADMIN_PERMISSIONS);

/** The API's `AdminRole`, ordered most → least authority as the Backend orders it. */
export type AdminRole = Schemas['AdminRole'];

export const ADMIN_ROLES = {
  FULL_ADMIN: 'full_admin',
  PROJECT_LEAD: 'project_lead',
  DOCUMENT_AUDITOR: 'document_auditor',
} as const satisfies Record<string, AdminRole>;
const _adminRolesCovered: Covers<AdminRole, (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES]> = true;

export const ALL_ADMIN_ROLES: readonly AdminRole[] = Object.values(ADMIN_ROLES);

/**
 * Mirrors the Backend's ADMIN_ROLE_PERMISSIONS (admin.dto.ts).
 * Keep in sync whenever the Backend adds a role or permission.
 */
export const ADMIN_ROLE_PERMISSIONS: Record<string, readonly AdminPermission[]> = {
  full_admin: [...ALL_ADMIN_PERMISSIONS],
  project_lead: [
    ADMIN_PERMISSIONS.ORGS_VERIFY,
    ADMIN_PERMISSIONS.DOCUMENTS_AUDIT,
    ADMIN_PERMISSIONS.TALENTS_MANAGE,
    ADMIN_PERMISSIONS.ORGS_MANAGE,
    ADMIN_PERMISSIONS.AUDIT_LOG_READ,
    ADMIN_PERMISSIONS.REPORTS_READ,
    ADMIN_PERMISSIONS.SUPPORT_MANAGE,
  ],
  // The support inbox goes with the review seat: whoever answers a verification
  // request is who the requester writes to when it stalls.
  document_auditor: [
    ADMIN_PERMISSIONS.ORGS_VERIFY,
    ADMIN_PERMISSIONS.DOCUMENTS_AUDIT,
    ADMIN_PERMISSIONS.SUPPORT_MANAGE,
  ],
};

export function permissionsForAdminRole(role: string): readonly AdminPermission[] {
  return ADMIN_ROLE_PERMISSIONS[role] ?? [];
}

export function hasAdminPermission(granted: readonly string[], required: AdminPermission): boolean {
  if (!granted || granted.length === 0) return false;
  // The Backend sends the permission strings themselves, not role names.
  // A wildcard '*' is an escape hatch for tests and local dev only.
  if (granted.includes('*')) return true;
  return granted.includes(required);
}
